// The country datasets were authored per-country rather than against a fixed
// schema, so the same piece of information arrives under many different keys
// ("Safety Level" / "Security Level" / "Level of Safety" / "Nivel de
// Seguridad", "Province Capitals" / "Capital of each Province" / "Capital de
// cada Región"...). Rendering those raw keys straight into the page is what
// made the country view read as a pile of unrelated rows.
//
// This module maps every known key onto a small set of canonical fields, and
// groups those fields into topical sections the page can lay out side by side.
// Anything unrecognised still surfaces (in the "extra" section, under its own
// original key) so no data is ever silently dropped.

export type CountrySectionId =
  | "geography"
  | "society"
  | "government"
  | "culture"
  | "divisions"
  | "extra";

// "divisions" goes last because it's the only section that can hold ~86
// province→capital pairs; the page renders it as a full-width block so those
// pairs get real horizontal room instead of a cramped scroll box.
export const COUNTRY_SECTION_ORDER: CountrySectionId[] = [
  "geography",
  "society",
  "government",
  "culture",
  "extra",
  "divisions",
];

export const COUNTRY_SECTION_META: Record<
  CountrySectionId,
  { emoji: string; es: string; en: string }
> = {
  geography: {
    emoji: "🏔️",
    es: "Geografía y naturaleza",
    en: "Geography & nature",
  },
  society: { emoji: "👥", es: "Gente y sociedad", en: "People & society" },
  government: {
    emoji: "🏛️",
    es: "Gobierno y economía",
    en: "Government & economy",
  },
  culture: { emoji: "🎭", es: "Cultura y viaje", en: "Culture & travel" },
  divisions: {
    emoji: "🗺️",
    es: "División administrativa",
    en: "Administrative divisions",
  },
  extra: { emoji: "📌", es: "Información extra", en: "Extra information" },
};

interface FieldRule {
  id: string;
  section: CountrySectionId;
  es: string;
  en: string;
  // Normalized (lowercase, unaccented) full-key matches.
  exact?: string[];
  // Normalized substrings; matched only after every `exact` rule has been
  // tried, so a specific key never gets swallowed by a broader rule.
  includes?: string[];
}

// Order matters within each pass: the first rule that matches a key wins, so
// narrower rules ("tourist landscapes") precede broader ones ("landscapes").
const FIELD_RULES: FieldRule[] = [
  // --- geography -----------------------------------------------------------
  // Climate used to be a quick fact, back when it was one sentence. It now
  // carries measured figures and a temperature band, which need the room a
  // section card gives them.
  {
    id: "climate",
    section: "geography",
    es: "Clima",
    en: "Climate",
    exact: ["climate", "clima"],
  },
  {
    id: "biomes",
    section: "geography",
    es: "Biomas",
    en: "Biomes",
    exact: ["biomes", "biomas"],
  },
  {
    id: "touristLandscapes",
    section: "geography",
    es: "Paisajes turísticos",
    en: "Tourist landscapes",
    includes: [
      "tourist landscapes",
      "touristic landscapes",
      "paisajes turisticos",
    ],
  },
  {
    id: "landscapes",
    section: "geography",
    es: "Paisajes",
    en: "Landscapes",
    includes: ["landscape", "paisaje"],
  },
  {
    id: "naturalResources",
    section: "geography",
    es: "Riquezas naturales",
    en: "Natural resources",
    includes: ["natural resources", "riquezas naturales", "recursos naturales"],
  },
  {
    id: "seas",
    section: "geography",
    es: "Mares y océanos",
    en: "Seas & oceans",
    includes: ["seas and oceans", "mares y oceanos"],
  },
  {
    id: "borders",
    section: "geography",
    es: "Países limítrofes",
    en: "Bordering countries",
    includes: [
      "bordering countries",
      "neighboring countries",
      "neighbouring countries",
      "paises limitrofes",
    ],
  },

  // --- society -------------------------------------------------------------
  {
    id: "religions",
    section: "society",
    es: "Religiones",
    en: "Religions",
    includes: ["religion"],
  },
  {
    id: "mortality",
    section: "society",
    es: "Tasa de mortalidad",
    en: "Mortality rate",
    includes: ["mortality", "mortalidad"],
  },
  {
    id: "education",
    section: "society",
    es: "Educación",
    en: "Education",
    includes: ["education", "educacion"],
  },
  {
    id: "safety",
    section: "society",
    es: "Nivel de seguridad",
    en: "Safety level",
    includes: ["safety", "security", "seguridad"],
  },
  {
    id: "indexes",
    section: "society",
    // "Índices" was right when these were vague levels; they're now measured
    // figures with a year and a source.
    es: "Indicadores",
    en: "Indicators",
    exact: ["indexes", "indicators", "indices"],
  },
  {
    id: "languages",
    section: "society",
    es: "Idiomas",
    en: "Languages",
    includes: ["language", "idioma"],
  },

  // --- government & economy ------------------------------------------------
  {
    id: "governmentType",
    section: "government",
    es: "Tipo de gobierno",
    en: "Government type",
    includes: ["government", "gobierno"],
  },
  {
    id: "leader",
    section: "government",
    es: "Jefatura de Estado",
    en: "Head of state",
    includes: [
      "president",
      "presidente",
      "prime minister",
      "primer ministro",
      "head of state",
      "jefe de estado",
      "leader",
      "lider",
      "king",
      "rey",
      "emir",
      "monarch",
      "monarca",
    ],
  },
  {
    id: "exports",
    section: "government",
    es: "Exportaciones",
    en: "Exports",
    includes: ["export", "materias primas", "commodities"],
  },

  // --- culture & travel ----------------------------------------------------
  // Both before the broader "patrimonio" rule, which would otherwise swallow
  // them and leave differently-sourced fields sharing one label.
  {
    id: "intangibleHeritage",
    section: "culture",
    es: "Patrimonio Inmaterial (UNESCO)",
    en: "Intangible Heritage (UNESCO)",
    includes: ["intangible heritage", "patrimonio inmaterial"],
  },
  {
    id: "worldHeritage",
    section: "culture",
    es: "Patrimonio Mundial (UNESCO)",
    en: "World Heritage (UNESCO)",
    includes: ["world heritage", "patrimonio mundial"],
  },
  {
    id: "heritage",
    section: "culture",
    es: "Patrimonio cultural",
    en: "Cultural heritage",
    includes: ["cultural heritage", "patrimonio"],
  },
  {
    id: "foods",
    section: "culture",
    es: "Comidas típicas",
    en: "Traditional foods",
    includes: ["foods", "dishes", "comidas", "gastronom"],
  },
  {
    id: "music",
    section: "culture",
    es: "Música",
    en: "Music",
    exact: ["music", "musica"],
  },
  {
    id: "sport",
    section: "culture",
    es: "Deporte",
    en: "Sport",
    exact: ["sport", "sports", "deporte", "deportes"],
  },
  {
    id: "attractions",
    section: "culture",
    es: "Atractivos turísticos",
    en: "Tourist attractions",
    includes: ["tourist attractions", "atractivos turisticos"],
  },

  // --- administrative divisions -------------------------------------------
  // Before the broader "divisions" rule: "Province Capitals" and "Capital de
  // cada Región" both name a division, but they hold the capital mapping.
  {
    id: "divisionCapitals",
    section: "divisions",
    es: "Capitales",
    en: "Capitals",
    includes: [
      "capitals",
      "capital de cada",
      "capital de los",
      "capital of each",
      "municipios de cada",
      "municipalities",
    ],
  },
  {
    id: "divisions",
    section: "divisions",
    es: "Divisiones",
    en: "Divisions",
    includes: [
      "province",
      "provincia",
      "department",
      "departamento",
      "district",
      "distrito",
      "region",
      "administrative",
      "prefectura",
      "condado",
      "state",
      "municipi",
    ],
  },

  // --- extra ---------------------------------------------------------------
  {
    id: "extraInfo",
    section: "extra",
    es: "Información extra",
    en: "Extra information",
    includes: ["extra information", "informacion extra"],
  },
  {
    id: "officialName",
    section: "extra",
    es: "Nombre oficial",
    en: "Official name",
    includes: ["nombre oficial", "official name"],
  },
];

export const normalizeFieldKey = (key: string) =>
  key
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const matchRule = (normalizedKey: string): FieldRule | undefined =>
  FIELD_RULES.find((rule) => rule.exact?.includes(normalizedKey)) ??
  FIELD_RULES.find((rule) =>
    rule.includes?.some((needle) => normalizedKey.includes(needle))
  );

export interface CountryField {
  /** Canonical id, or the raw key when nothing matched. */
  id: string;
  /** Original dataset key, kept for screen-reader text and React keys. */
  rawKey: string;
  label: string;
  value: unknown;
}

export interface CountrySectionContent {
  id: CountrySectionId;
  emoji: string;
  title: string;
  fields: CountryField[];
}

/**
 * Groups a country's leftover entries (everything not already shown as a
 * quick fact) into topical sections, relabelled in the reading language.
 */
export const groupCountryFields = (
  entries: Array<[string, unknown]>,
  isSpanish: boolean
): CountrySectionContent[] => {
  const bySection = new Map<CountrySectionId, CountryField[]>();

  entries.forEach(([rawKey, value]) => {
    const rule = matchRule(normalizeFieldKey(rawKey));
    const section: CountrySectionId = rule?.section ?? "extra";
    const field: CountryField = {
      id: rule?.id ?? rawKey,
      rawKey,
      // Unmatched keys keep their original wording — better an untranslated
      // label than hiding the value entirely.
      label: rule ? (isSpanish ? rule.es : rule.en) : rawKey,
      value,
    };

    const existing = bySection.get(section);
    if (existing) existing.push(field);
    else bySection.set(section, [field]);
  });

  return COUNTRY_SECTION_ORDER.map((id) => {
    const fields = bySection.get(id) ?? [];
    const meta = COUNTRY_SECTION_META[id];
    return {
      id,
      emoji: meta.emoji,
      title: isSpanish ? meta.es : meta.en,
      fields,
    };
  }).filter((section) => section.fields.length > 0);
};

const isScalar = (value: unknown): value is string | number =>
  typeof value === "string" || typeof value === "number";

export type PairMapValue = string | string[];

/**
 * True for `{ "Buenos Aires": "La Plata", ... }`-shaped values, and also for
 * the mixed shape fifteen countries use, where a sub-grouping is a list
 * rather than a single seat:
 *
 *   { "Gozo Region (seat)": "Victoria", "Local councils (68)": ["Attard", …] }
 *
 * Requiring every value to be scalar made those blocks fall back to the
 * generic collapsed definition list — two narrow columns for 68 names — and
 * took the pairs down with them.
 */
export const isPairMap = (
  value: unknown
): value is Record<string, PairMapValue> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.keys(value as Record<string, unknown>).length > 0 &&
  Object.values(value as Record<string, unknown>).every(
    (v) => isScalar(v) || (Array.isArray(v) && v.every(isScalar))
  );

export interface SplitPairs {
  /** "region → seat" entries, rendered as tiles. */
  pairs: Array<[string, string]>;
  /** "grouping → many members" entries, rendered as their own labelled grid. */
  lists: Array<[string, string[]]>;
}

/** Separates the two shapes so each can get the layout it deserves. */
export const splitPairsAndLists = (
  entries: Array<[string, PairMapValue]>
): SplitPairs => {
  const pairs: Array<[string, string]> = [];
  const lists: Array<[string, string[]]> = [];

  entries.forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const items = value.map(String).filter((item) => item.trim().length > 0);
      if (items.length > 0) lists.push([key, items]);
    } else {
      pairs.push([key, String(value)]);
    }
  });

  return { pairs, lists };
};

// Inside a dict-valued field these keys don't describe one entry — they
// caption or footnote the whole block ("Russia is made up of 85 federal
// subjects…"). They need prose treatment, not a tile/row like the rest.
const BLOCK_CAPTION_KEYS = new Set(["detail", "detalle"]);
const BLOCK_NOTE_KEYS = new Set(["note", "nota"]);

export interface SplitBlock<T> {
  captions: string[];
  notes: string[];
  entries: Array<[string, T]>;
}

export const splitBlockMeta = <T>(obj: Record<string, T>): SplitBlock<T> => {
  const captions: string[] = [];
  const notes: string[] = [];
  const entries: Array<[string, T]> = [];

  Object.entries(obj).forEach(([key, value]) => {
    const normalized = key.trim().toLowerCase();
    // Only plain prose gets lifted out; a "Detail" holding a list or an
    // object is real data and stays in the block.
    if (typeof value === "string" && BLOCK_CAPTION_KEYS.has(normalized)) {
      captions.push(value);
    } else if (typeof value === "string" && BLOCK_NOTE_KEYS.has(normalized)) {
      notes.push(value);
    } else {
      entries.push([key, value]);
    }
  });

  return { captions, notes, entries };
};
